import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { formatBRL } from "@/lib/format";
import { TID } from "@/constants/testIds";

const FEATURE_LABELS = [
  { key: "ai", label: "Inteligência Artificial" },
  { key: "appcc", label: "APPCC" },
  { key: "audits", label: "Auditorias" },
  { key: "whatsapp", label: "Notificações WhatsApp" },
  { key: "offline", label: "Modo Offline" },
  { key: "multi_unit", label: "Multi-unidade" },
  { key: "stock_control", label: "Controle de Estoque" },
];

const EXTRAS = {
  starter: ["Suporte por email"],
  professional: ["Suporte prioritário"],
  business: ["Suporte prioritário"],
  enterprise: ["Onboarding dedicado", "SLA 99,9%"],
};

const limitText = (n, singular, plural, unlimited) => {
  if (n === null || n === undefined) return null;
  if (n >= 9999) return unlimited;
  return `${n} ${n === 1 ? singular : plural}`;
};

const planFeatures = (plan) => {
  const l = plan.limits || {};
  const out = [
    limitText(l.units, "unidade", "unidades", "Unidades ilimitadas"),
    limitText(l.users, "usuário", "usuários", "Usuários ilimitados"),
    limitText(l.checklists, "checklist", "checklists", "Checklists ilimitados"),
    l.storage_mb >= 500000 ? "Storage ilimitado" : l.storage_mb ? (l.storage_mb >= 1024 ? `${Math.round(l.storage_mb / 1024)} GB de storage` : `${l.storage_mb} MB de storage`) : null,
  ].filter(Boolean);
  for (const f of FEATURE_LABELS) {
    const on = f.key === "stock_control" ? plan.features?.stock_control !== false : !!plan.features?.[f.key];
    if (on) out.push(f.label);
  }
  return [...out, ...(EXTRAS[plan.slug] || [])];
};

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        setPlans(data);
      } catch (err) {
        console.error("plans load failed", err);
      }
    })();
  }, []);

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast.info("Crie sua conta para assinar um plano.");
      navigate("/register");
      return;
    }
    setCheckoutLoading(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id: plan.id,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Sessão de pagamento inválida.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao iniciar pagamento");
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-stone-50/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Ultra" className="h-9 w-9 rounded-lg" />
            <span className="font-display text-xl font-extrabold">Ultra</span>
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard">
                <Button variant="outline" className="border-stone-300 bg-white" data-testid="pricing-to-dashboard">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="ghost" className="font-body" data-testid="pricing-to-login">
                  Entrar
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <section data-testid={TID.pricing} className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="max-w-3xl">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
            Planos & Preços
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            Comece grátis. Cresça quando precisar.
          </h1>
          <p className="mt-4 font-body text-stone-600">
            Pagamento mensal ou anual via Stripe. Cancele quando quiser.
          </p>
        </div>

        <div className="mt-8 inline-flex items-center bg-white border border-stone-200 rounded-lg p-1" data-testid="cycle-toggle">
          <button
            onClick={() => setCycle("monthly")}
            data-testid="cycle-monthly"
            className={`px-4 py-2 text-sm rounded-md font-body transition-colors ${cycle === "monthly" ? "bg-emerald-800 text-white" : "text-stone-700 hover:bg-stone-100"}`}
          >
            Mensal
          </button>
          <button
            onClick={() => setCycle("yearly")}
            data-testid="cycle-yearly"
            className={`px-4 py-2 text-sm rounded-md font-body transition-colors ${cycle === "yearly" ? "bg-emerald-800 text-white" : "text-stone-700 hover:bg-stone-100"}`}
          >
            Anual <Badge className="ml-2 bg-amber-500 text-stone-950 hover:bg-amber-500">-17%</Badge>
          </button>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan, idx) => {
            const price = cycle === "yearly" ? plan.yearly_price : plan.monthly_price;
            const highlight = plan.slug === "professional";
            const features = planFeatures(plan);
            return (
              <Card
                key={plan.id}
                data-testid={`${TID.pricingPlan}-${plan.slug}`}
                className={`p-6 rounded-2xl border bg-white relative flex flex-col ${highlight ? "border-emerald-700 ring-2 ring-emerald-700/30" : "border-stone-200"}`}
              >
                {highlight && (
                  <Badge className="absolute -top-3 left-6 bg-emerald-700 text-white">Mais popular</Badge>
                )}
                <div className="font-display text-xl font-bold tracking-tight">{plan.name}</div>
                <p className="mt-1 text-sm text-stone-500 font-body min-h-[40px]">{plan.description}</p>
                <div className="mt-5">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-4xl font-extrabold tracking-tight">
                      {formatBRL(price)}
                    </span>
                    <span className="text-xs text-stone-500 font-body">/ {cycle === "yearly" ? "ano" : "mês"}</span>
                  </div>
                </div>
                <ul className="mt-5 space-y-2 text-sm font-body flex-1">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-stone-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className={`mt-6 w-full h-11 font-body ${highlight ? "bg-emerald-800 hover:bg-emerald-900 text-white" : "bg-stone-950 hover:bg-stone-800 text-white"}`}
                  onClick={() => handleSubscribe(plan)}
                  disabled={!!checkoutLoading}
                  data-testid={`${TID.pricingCheckout}-${plan.slug}`}
                >
                  {checkoutLoading === plan.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Assinar agora <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </Card>
            );
          })}
        </div>

        <p className="mt-8 text-xs text-stone-500 font-body">
          Pagamentos processados de forma segura pela Stripe. Valor cobrado em BRL.
        </p>
      </section>
    </div>
  );
}
